/**
 * Sessions Report Grid Component
 * Matches Flutter's session grid layout with dynamic columns and pagination
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  flexRender,
  SortingState,
  RowSelectionState,
  VisibilityState,
  GroupingState,
  ExpandedState,
  ColumnSizingState,
} from '@tanstack/react-table';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
// Note: Using raw HTML table elements instead of shadcn Table
// because shadcn Table has nested overflow-auto div that breaks sticky header
import { SessionGridRow } from '../types/session-types';
import { TariffLevelMapping } from '../types/api-types';
import { TFunction } from 'i18next';
import {
  getSessionsGridColumns,
  sessionsGridColumns,
  defaultSorting,
  defaultColumnVisibility,
  ColumnGroup,
} from './SessionsGridColumns';
import { GridLoadingState } from './GridLoadingState';
import { GridEmptyState } from './GridEmptyState';
import { GridErrorState } from './GridErrorState';

interface SessionsGridProps {
  data: SessionGridRow[];
  isLoading: boolean;
  error?: string;
  totalCount?: number;
  onRowSelect?: (session: SessionGridRow) => void;
  onRefresh?: () => void;
  selectedSessionId?: number;
  columnVisibility?: VisibilityState;
  // Pagination
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  // Grouping (HIVE-6234)
  groupByLocation?: boolean;
  // Tariff levels for dynamic column headers
  tariffLevels?: TariffLevelMapping[];
  // Column groups for VAT Include logic
  enabledColumnGroups?: ColumnGroup[];
  // Translation function for column headers
  t?: TFunction;
}

/**
 * Main grid component for displaying charging sessions
 * Uses TanStack Table for sorting and row selection
 */
export const SessionsGrid = ({
  data,
  isLoading,
  error,
  totalCount = 0,
  onRowSelect,
  onRefresh,
  selectedSessionId,
  columnVisibility: externalColumnVisibility,
  currentPage,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  groupByLocation = false,
  tariffLevels = [],
  enabledColumnGroups = [],
  t,
}: SessionsGridProps) => {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    externalColumnVisibility || defaultColumnVisibility
  );
  // HIVE-6234: Grouping state - reset expanded when grouping toggles
  const [expanded, setExpanded] = useState<ExpandedState>({});
  // Column resizing state - allows user to adjust column widths like Flutter
  // Force Location column to 100px fixed width
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({
    kioskActorHierarchy: 100,
  });

  // Generate columns with dynamic tariff level headers (matching Flutter)
  // Filter out tariff levels with no data
  // Pass VAT Include state to calculate VAT-inclusive prices for tariff levels
  const columns = useMemo(
    () => getSessionsGridColumns(tariffLevels, data, enabledColumnGroups.includes('vatInclude'), t),
    [tariffLevels, data, enabledColumnGroups, t]
  );

  // Force reset Location column width on mount
  useEffect(() => {
    setColumnSizing({ kioskActorHierarchy: 100 });
  }, []);

  // Reset expanded state when grouping is toggled
  useEffect(() => {
    setExpanded({});
  }, [groupByLocation]);

  // Update internal state when external visibility changes
  useEffect(() => {
    if (externalColumnVisibility) {
      setColumnVisibility(externalColumnVisibility);
    }
  }, [externalColumnVisibility]);

  // Calculate totals for footer
  // HIVE-6234: Updated to work with grouped view - only sum visible session rows
  const totals = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Calculate component totals first
    const costTotal = data.reduce((sum, row) => sum + (row.cost || 0), 0);
    const vatCostTotal = data.reduce((sum, row) => sum + (row.vatCost || 0), 0);

    const connectionFeeWithoutVatTotal = data.reduce((sum, row) => sum + (row.connectionFeeWithoutVat || 0), 0);
    const connectionFeeVatTotal = data.reduce((sum, row) => sum + (row.connectionFeeVat || 0), 0);

    const timeFeeWithoutVatTotal = data.reduce((sum, row) => sum + (row.timeFeeWithoutVat || 0), 0);
    const timeFeeVatTotal = data.reduce((sum, row) => sum + (row.timeFeeVat || 0), 0);

    const idleFeeWithoutVatTotal = data.reduce((sum, row) => sum + (row.idleFeeWithoutVat || 0), 0);
    const idleFeeVatTotal = data.reduce((sum, row) => sum + (row.idleFeeVat || 0), 0);

    const totalPaymentCostExcVatTotal = data.reduce((sum, row) => sum + (row.totalPaymentCostExcVat || 0), 0);
    const totalPaymentVatTotal = data.reduce((sum, row) => sum + (row.totalPaymentVat || 0), 0);

    const discountExcVatTotal = data.reduce((sum, row) => sum + (row.discountExcVat || 0), 0);
    const discountVatTotal = data.reduce((sum, row) => sum + (row.discountVat || 0), 0);

    const totalPaymentAfterDiscountExcVatTotal = data.reduce((sum, row) => sum + (row.totalPaymentAfterDiscountExcVat || 0), 0);
    const totalPaymentAfterDiscountVatTotal = data.reduce((sum, row) => sum + (row.totalPaymentAfterDiscountVat || 0), 0);

    return {
      totalEnergy: data.reduce((sum, row) => sum + (row.totalEnergy || 0), 0),
      cost: costTotal,
      vatCost: vatCostTotal,
      // Calculate settlementValue as Excl + VAT (not summed directly)
      settlementValue: costTotal + vatCostTotal,

      // Flat Fee (Connection Fee)
      connectionFeeWithoutVat: connectionFeeWithoutVatTotal,
      connectionFeeVat: connectionFeeVatTotal,
      // Calculate connectionFee as Excl + VAT
      connectionFee: connectionFeeWithoutVatTotal + connectionFeeVatTotal,

      // Time Fee
      timeFeeWithoutVat: timeFeeWithoutVatTotal,
      timeFeeVat: timeFeeVatTotal,
      // Calculate timeFee as Excl + VAT
      timeFee: timeFeeWithoutVatTotal + timeFeeVatTotal,

      // Idle Fee
      idleFeeWithoutVat: idleFeeWithoutVatTotal,
      idleFeeVat: idleFeeVatTotal,
      // Calculate idleFee as Excl + VAT
      idleFee: idleFeeWithoutVatTotal + idleFeeVatTotal,

      // Total Fees (before discount)
      totalPaymentCostExcVat: totalPaymentCostExcVatTotal,
      totalPaymentVat: totalPaymentVatTotal,
      // Calculate totalPaymentCostIncVat as Excl + VAT
      totalPaymentCostIncVat: totalPaymentCostExcVatTotal + totalPaymentVatTotal,

      // Discount
      discountExcVat: discountExcVatTotal,
      discountVat: discountVatTotal,
      // Calculate discountIncVat as Excl + VAT
      discountIncVat: discountExcVatTotal + discountVatTotal,

      // Total Cost (after discount)
      totalPaymentAfterDiscountExcVat: totalPaymentAfterDiscountExcVatTotal,
      totalPaymentAfterDiscountVat: totalPaymentAfterDiscountVatTotal,
      // Calculate totalPaymentAfterDiscountIncVat as Excl + VAT
      totalPaymentAfterDiscountIncVat: totalPaymentAfterDiscountExcVatTotal + totalPaymentAfterDiscountVatTotal,

      // Tariff Levels (L1-L9)
      l1UnitCost: data.reduce((sum, row) => sum + (row.l1UnitCost || 0), 0),
      l1Energy: data.reduce((sum, row) => sum + (row.l1Energy || 0), 0),
      l1Cost: data.reduce((sum, row) => sum + (row.l1Cost || 0), 0),
      l2UnitCost: data.reduce((sum, row) => sum + (row.l2UnitCost || 0), 0),
      l2Energy: data.reduce((sum, row) => sum + (row.l2Energy || 0), 0),
      l2Cost: data.reduce((sum, row) => sum + (row.l2Cost || 0), 0),
      l3UnitCost: data.reduce((sum, row) => sum + (row.l3UnitCost || 0), 0),
      l3Energy: data.reduce((sum, row) => sum + (row.l3Energy || 0), 0),
      l3Cost: data.reduce((sum, row) => sum + (row.l3Cost || 0), 0),
      l4UnitCost: data.reduce((sum, row) => sum + (row.l4UnitCost || 0), 0),
      l4Energy: data.reduce((sum, row) => sum + (row.l4Energy || 0), 0),
      l4Cost: data.reduce((sum, row) => sum + (row.l4Cost || 0), 0),
      l5UnitCost: data.reduce((sum, row) => sum + (row.l5UnitCost || 0), 0),
      l5Energy: data.reduce((sum, row) => sum + (row.l5Energy || 0), 0),
      l5Cost: data.reduce((sum, row) => sum + (row.l5Cost || 0), 0),
      l6UnitCost: data.reduce((sum, row) => sum + (row.l6UnitCost || 0), 0),
      l6Energy: data.reduce((sum, row) => sum + (row.l6Energy || 0), 0),
      l6Cost: data.reduce((sum, row) => sum + (row.l6Cost || 0), 0),
      l7UnitCost: data.reduce((sum, row) => sum + (row.l7UnitCost || 0), 0),
      l7Energy: data.reduce((sum, row) => sum + (row.l7Energy || 0), 0),
      l7Cost: data.reduce((sum, row) => sum + (row.l7Cost || 0), 0),
      l8UnitCost: data.reduce((sum, row) => sum + (row.l8UnitCost || 0), 0),
      l8Energy: data.reduce((sum, row) => sum + (row.l8Energy || 0), 0),
      l8Cost: data.reduce((sum, row) => sum + (row.l8Cost || 0), 0),
      l9UnitCost: data.reduce((sum, row) => sum + (row.l9UnitCost || 0), 0),
      l9Energy: data.reduce((sum, row) => sum + (row.l9Energy || 0), 0),
      l9Cost: data.reduce((sum, row) => sum + (row.l9Cost || 0), 0),
    };
  }, [data]);

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  // HIVE-6234: Configure grouping based on groupByLocation prop (memoized to prevent re-renders)
  const grouping: GroupingState = useMemo(
    () => (groupByLocation ? ['locationName'] : []),
    [groupByLocation]
  );

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 60,
      maxSize: 500,
      enableResizing: true,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    enableColumnResizing: true,
    onColumnSizingChange: setColumnSizing,
    groupedColumnMode: false,
    getRowId: (row) => row.sessionId.toString(),
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      grouping,
      expanded,
      columnSizing,
    },
  });

  // Handle loading state - wrap in same container structure to maintain height
  if (isLoading && data.length === 0) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        <GridLoadingState rowCount={10} columnCount={8} />
      </div>
    );
  }

  // Handle error state - wrap in same container structure to maintain height
  if (error) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        <GridErrorState
          error={error}
          onRetry={onRefresh}
          title="Failed to Load Sessions"
        />
      </div>
    );
  }

  // Handle empty state - wrap in same container structure to maintain height
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        <GridEmptyState
          onRefresh={onRefresh}
          title="No Sessions Found"
          description="No charging sessions found for the selected date range. Try adjusting your filters or check back later."
        />
      </div>
    );
  }

  // Get visible leaf columns for totals row (excludes parent group columns)
  const visibleColumns = table.getVisibleLeafColumns();

  // Generate page numbers to show (matching Flutter screenshot: 1 2 3 4 > >|)
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;

    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Table container - fills available space, scrolls internally */}
      <div
        className={`border-x border-t border-[#E8E8E8] bg-white flex-1 min-h-0 overflow-auto sticky-scrollbar ${isLoading ? 'opacity-50' : ''}`}
        data-testid="sessions-grid"
      >
        {/* HIVE-6307: Using raw table for sticky header support - Table component has nested overflow-auto that breaks sticky */}
        <table className="w-full min-w-max table-auto caption-bottom text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-[#F1F1F1]">
            {table.getHeaderGroups().map((headerGroup, groupIndex) => {
              const isLastHeaderRow = groupIndex === table.getHeaderGroups().length - 1;
              const isFirstHeaderRow = groupIndex === 0;
              return (
                <tr key={headerGroup.id} className={`bg-[#F1F1F1] transition-colors ${isLastHeaderRow ? 'border-b' : ''}`}>
                  {headerGroup.headers.map((header) => {
                    const isGroupHeader = header.subHeaders && header.subHeaders.length > 0;
                    const isChildOfGroup = !isGroupHeader && !!header.column.parent;
                    // Standalone headers in SECOND row that aren't children = need centering
                    const isStandaloneHeader = !isGroupHeader && !isChildOfGroup && !isFirstHeaderRow;
                    const shouldUseRowSpan = isStandaloneHeader || (!isGroupHeader && header.rowSpan);
                    const hasRowSpan = isStandaloneHeader;

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        rowSpan={isStandaloneHeader ? 2 : (isGroupHeader ? 1 : undefined)}
                        className={`relative text-xs font-medium text-[#262626] border-l border-r border-gray-300 !bg-[#F1F1F1] ${
                          isStandaloneHeader ? 'text-center' : isChildOfGroup ? 'px-2 py-0' : 'px-3 py-1.5'
                        } ${
                          isGroupHeader ? 'text-center' : ''
                        } ${
                          isChildOfGroup ? 'border-t border-gray-300' : ''
                        } ${
                          !isGroupHeader && !isStandaloneHeader && (header.column.columnDef.meta as any)?.align === 'right'
                            ? 'text-right'
                            : !isGroupHeader && !isStandaloneHeader && (header.column.columnDef.meta as any)?.align === 'center'
                              ? 'text-center'
                              : !isGroupHeader && !isStandaloneHeader
                                ? 'text-left'
                                : ''
                        }`}
                        style={{
                          width: isStandaloneHeader ? 'auto' : header.getSize(),
                          minWidth: isStandaloneHeader ? '180px' : header.getSize(),
                          backgroundColor: '#F1F1F1',
                          ...(isStandaloneHeader ? {
                            height: '40px',
                            paddingTop: '0px',
                            paddingBottom: '30px',
                            paddingLeft: '3px',
                            paddingRight: '3px',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            fontSize: '13px',
                            fontWeight: '500',
                          } : {
                            verticalAlign: 'middle',
                            fontSize: '13px',
                            fontWeight: '500',
                          }),
                          ...(isChildOfGroup ? {
                            height: '32px',
                            lineHeight: '32px',
                          } : {}),
                          ...(isGroupHeader ? {
                            height: '32px',
                          } : {}),
                        }}
                      >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={`flex items-center ${
                            header.column.getCanSort() ? 'space-x-1' : ''
                          } ${
                            header.column.getCanSort()
                              ? 'cursor-pointer select-none hover:text-[#434343]'
                              : ''
                          } ${
                            isGroupHeader || hasRowSpan
                              ? 'justify-center'
                              : (header.column.columnDef.meta as any)?.align === 'center'
                                ? 'justify-center'
                                : (header.column.columnDef.meta as any)?.align === 'right'
                                  ? 'justify-end'
                                  : ''
                          }`}
                          style={hasRowSpan ? {} : {}}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {header.column.getCanSort() && (
                            <div className="flex flex-col">
                              <ChevronUp
                                className={`h-3 w-3 ${
                                  header.column.getIsSorted() === 'asc'
                                    ? 'text-[#FFCD00]'
                                    : 'text-gray-400'
                                }`}
                              />
                              <ChevronDown
                                className={`h-3 w-3 -mt-1 ${
                                  header.column.getIsSorted() === 'desc'
                                    ? 'text-[#FFCD00]'
                                    : 'text-gray-400'
                                }`}
                              />
                            </div>
                          )}
                        </div>
                        {/* Column resize handle - drag to resize columns like Flutter */}
                        {!header.isPlaceholder && !header.subHeaders?.length && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={`absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none ${
                              header.column.getIsResizing()
                                ? 'bg-[#FFCD00]'
                                : 'bg-gray-300 opacity-0 hover:opacity-100'
                            }`}
                            style={{
                              userSelect: 'none',
                            }}
                          />
                        )}
                      </>
                    )}
                  </th>
                );
                  })}
                </tr>
              );
            })}
          </thead>

          <tbody className="[&_tr:last-child]:border-0">
            {table.getRowModel().rows.map((row) => {
              // HIVE-6234: Check if this is a group header row
              if (row.getIsGrouped()) {
                const locationName = row.groupingValue as string;
                const sessionCount = row.subRows.length;
                const isExpanded = row.getIsExpanded();

                return (
                  <tr
                    key={row.id}
                    className="bg-[#F1F1F1] border-b border-[#E8E8E8] cursor-pointer hover:bg-[#E8E8E8]"
                    onClick={() => row.toggleExpanded()}
                  >
                    <td colSpan={visibleColumns.length} className="p-4">
                      <div className="flex items-center gap-2 font-medium text-sm text-[#262626]">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            row.toggleExpanded();
                          }}
                          className="flex items-center"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        <span>{locationName}</span>
                        <span className="text-gray-500 font-normal">
                          ({sessionCount} {sessionCount === 1 ? 'session' : 'sessions'})
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              }

              // Regular session row
              const session = row.original;
              const isSelected = session.sessionId === selectedSessionId;
              const isVirtual = session.isVirtualSession;

              return (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  data-testid="session-row"
                  className={`cursor-pointer transition-colors border-b border-[#E8E8E8] ${
                    isSelected
                      ? 'bg-[#FFFBEB] hover:bg-[#FEF8DF]'
                      : 'bg-white hover:bg-[#FAFAFA]'
                  }`}
                  onDoubleClick={() => {
                    row.toggleSelected();
                    onRowSelect?.(session);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`p-4 align-middle py-2 whitespace-nowrap ${
                        (cell.column.columnDef.meta as any)?.align === 'right'
                          ? 'text-right'
                          : (cell.column.columnDef.meta as any)?.align ===
                              'center'
                            ? 'text-center'
                            : ''
                      }`}
                      style={{
                        width: `${cell.column.getSize()}px`,
                        maxWidth: `${cell.column.getSize()}px`,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>

          {/* Totals Footer Row */}
          {totals && (
            <tfoot key="totals-footer" className="border-t bg-muted/50 [&>tr]:last:border-b-0">
              <tr key="totals-row" className="bg-[#F1F1F1] border-b transition-colors">
                {visibleColumns.map((column, index) => {
                  // First column shows "Sum total" label
                  if (index === 0) {
                    return (
                      <td key={column.id} className="p-4 align-middle text-sm">
                        Sum total
                      </td>
                    );
                  }

                  // Show totals for numeric columns
                  const columnId = column.id;
                  let value: number | null = null;

                  switch (columnId) {
                    case 'totalEnergy':
                      value = totals.totalEnergy;
                      break;
                    // Energy Fee columns
                    case 'energyCostExcVat':
                    case 'cost':
                      value = totals.cost;
                      break;
                    case 'energyVat':
                    case 'vatCost':
                      value = totals.vatCost;
                      break;
                    case 'energyCostIncVat':
                      value = totals.cost + totals.vatCost;
                      break;
                    // Flat Fee columns
                    case 'connectionFeeWithoutVat':
                      value = totals.connectionFeeWithoutVat;
                      break;
                    case 'connectionFeeVat':
                      value = totals.connectionFeeVat;
                      break;
                    case 'connectionFee':
                      value = totals.connectionFee;
                      break;
                    // Time Fee columns
                    case 'timeFeeWithoutVat':
                      value = totals.timeFeeWithoutVat;
                      break;
                    case 'timeFeeVat':
                      value = totals.timeFeeVat;
                      break;
                    case 'timeFee':
                      value = totals.timeFee;
                      break;
                    // Idle Fee columns
                    case 'idleFeeWithoutVat':
                      value = totals.idleFeeWithoutVat;
                      break;
                    case 'idleFeeVat':
                      value = totals.idleFeeVat;
                      break;
                    case 'idleFee':
                      value = totals.idleFee;
                      break;
                    // Settlement columns
                    case 'settlementExcVat':
                      value = totals.cost;
                      break;
                    case 'settlementVat':
                      value = totals.vatCost;
                      break;
                    case 'settlementIncVat':
                    case 'settlementValue':
                      value = totals.settlementValue;
                      break;
                    // Total Fees columns
                    case 'totalPaymentCostExcVat':
                      value = totals.totalPaymentCostExcVat;
                      break;
                    case 'totalPaymentVat':
                      value = totals.totalPaymentVat;
                      break;
                    case 'totalPaymentCostIncVat':
                      value = totals.totalPaymentCostIncVat;
                      break;
                    // Discount columns
                    case 'discountExcVat':
                      value = totals.discountExcVat;
                      break;
                    case 'discountVat':
                      value = totals.discountVat;
                      break;
                    case 'discountIncVat':
                      value = totals.discountIncVat;
                      break;
                    // Total Cost columns
                    case 'totalPaymentAfterDiscountExcVat':
                      value = totals.totalPaymentAfterDiscountExcVat;
                      break;
                    case 'totalPaymentAfterDiscountVat':
                      value = totals.totalPaymentAfterDiscountVat;
                      break;
                    case 'totalPaymentAfterDiscountIncVat':
                      value = totals.totalPaymentAfterDiscountIncVat;
                      break;
                    // Tariff Level L1
                    case 'l1UnitCost':
                      value = totals.l1UnitCost;
                      break;
                    case 'l1Energy':
                      value = totals.l1Energy;
                      break;
                    case 'l1Cost':
                      value = totals.l1Cost;
                      break;
                    // Tariff Level L2
                    case 'l2UnitCost':
                      value = totals.l2UnitCost;
                      break;
                    case 'l2Energy':
                      value = totals.l2Energy;
                      break;
                    case 'l2Cost':
                      value = totals.l2Cost;
                      break;
                    // Tariff Level L3
                    case 'l3UnitCost':
                      value = totals.l3UnitCost;
                      break;
                    case 'l3Energy':
                      value = totals.l3Energy;
                      break;
                    case 'l3Cost':
                      value = totals.l3Cost;
                      break;
                    // Tariff Level L4
                    case 'l4UnitCost':
                      value = totals.l4UnitCost;
                      break;
                    case 'l4Energy':
                      value = totals.l4Energy;
                      break;
                    case 'l4Cost':
                      value = totals.l4Cost;
                      break;
                    // Tariff Level L5
                    case 'l5UnitCost':
                      value = totals.l5UnitCost;
                      break;
                    case 'l5Energy':
                      value = totals.l5Energy;
                      break;
                    case 'l5Cost':
                      value = totals.l5Cost;
                      break;
                    // Tariff Level L6
                    case 'l6UnitCost':
                      value = totals.l6UnitCost;
                      break;
                    case 'l6Energy':
                      value = totals.l6Energy;
                      break;
                    case 'l6Cost':
                      value = totals.l6Cost;
                      break;
                    // Tariff Level L7
                    case 'l7UnitCost':
                      value = totals.l7UnitCost;
                      break;
                    case 'l7Energy':
                      value = totals.l7Energy;
                      break;
                    case 'l7Cost':
                      value = totals.l7Cost;
                      break;
                    // Tariff Level L8
                    case 'l8UnitCost':
                      value = totals.l8UnitCost;
                      break;
                    case 'l8Energy':
                      value = totals.l8Energy;
                      break;
                    case 'l8Cost':
                      value = totals.l8Cost;
                      break;
                    // Tariff Level L9
                    case 'l9UnitCost':
                      value = totals.l9UnitCost;
                      break;
                    case 'l9Energy':
                      value = totals.l9Energy;
                      break;
                    case 'l9Cost':
                      value = totals.l9Cost;
                      break;
                  }

                  return (
                    <td
                      key={column.id}
                      className={`p-4 align-middle text-sm ${
                        (column.columnDef.meta as any)?.align === 'right'
                          ? 'text-right'
                          : ''
                      }`}
                    >
                      {value !== null ? value.toFixed(2) : ''}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination Footer - Always visible at bottom */}
      <div className="flex-shrink-0 flex items-center justify-between px-2 py-3 border-x border-b border-[#E8E8E8] bg-white">
        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          {/* First Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(0)}
            disabled={currentPage === 0 || isLoading}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page Numbers */}
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'ghost'}
              size="sm"
              className={`h-8 w-8 p-0 ${
                currentPage === pageNum
                  ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]'
                  : 'text-[#262626] hover:bg-[#FFFBEB]'
              }`}
              onClick={() => onPageChange(pageNum)}
              disabled={isLoading}
            >
              {pageNum + 1}
            </Button>
          ))}

          {/* Next Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1 || isLoading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1 || isLoading}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>

          {/* Page Info */}
          <span className="text-sm text-[#606060] ml-2">
            {currentPage + 1} of {totalPages} pages
          </span>
        </div>

        {/* Right side - showing range and page size */}
        <div className="flex items-center gap-4">
          {/* Range Info */}
          <span className="text-sm text-[#606060]">
            {startItem}-{endItem} of {totalCount.toLocaleString()}
          </span>

          {/* Rows per page */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#606060]">Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};
